import React, { useState, useEffect, useRef } from 'react';
import { BotConfig } from '../types';
import { useLiveApi } from '../hooks/useLiveApi';
import { useChat } from '../hooks/useChat';

interface WidgetPreviewProps {
  config: BotConfig;
  isEmbed?: boolean;
  apiKey?: string;
  hidePreview?: boolean;
}

type Tab = 'voice' | 'chat';

const WidgetPreview: React.FC<WidgetPreviewProps> = ({ config, isEmbed = false, apiKey, hidePreview = false }) => {
  const [isOpen, setIsOpen] = useState(() => {
    if (isEmbed) return true; 
    return typeof window !== 'undefined' && window.innerWidth >= 768;
  });

  const [activeTab, setActiveTab] = useState<Tab>('voice');
  const [chatInput, setChatInput] = useState('');
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEmbed) {
      window.parent.postMessage({ type: 'gemini-widget-resize', isOpen }, '*');
    }
  }, [isOpen, isEmbed]);

  const { isConnected, isSpeaking, isBotSpeaking, volume, transcripts, error: voiceError, reconnect } = useLiveApi({
    config,
    isOpen: isOpen && activeTab === 'voice',
    apiKey
  });

  const { messages: chatMessages, sendMessage, isLoading: isChatLoading } = useChat(config, apiKey);

  // Auto-scroll logic
  const scrollToBottom = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(messagesEndRef); }, [transcripts]);
  useEffect(() => { scrollToBottom(chatEndRef); }, [chatMessages, isChatLoading]);

  const handleSendChat = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (chatInput.trim() && !isChatLoading) {
      sendMessage(chatInput);
      setChatInput('');
    }
  };

  const showPreview = !isEmbed && !hidePreview;

  // Root Classes
  let rootClass = "relative flex flex-col";
  if (isEmbed) {
    rootClass += " w-full h-full";
  } else if (hidePreview) {
    rootClass += " fixed inset-0 pointer-events-none z-[9999]";
  } else {
    rootClass += " flex-grow bg-gray-100 items-center justify-center";
  }

  const widgetContainerClass = `
    z-50 flex flex-col items-end space-y-4 
    ${hidePreview ? 'pointer-events-auto' : ''} 
    ${(!isEmbed && !hidePreview) ? 'absolute bottom-6 right-6' : ''}
    ${isEmbed ? 'fixed bottom-4 right-4' : ''} 
    ${hidePreview ? 'absolute bottom-6 right-6' : ''}
  `;

  // Helper to generate dynamic gradient style
  const gradientStyle = {
    background: `linear-gradient(135deg, ${config.themeColor}, ${adjustColorBrightness(config.themeColor, -20)})`
  };

  return (
    <div className={rootClass}>
      
      {showPreview && (
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          {config.previewUrl ? (
            <iframe 
              src={config.previewUrl} 
              className="w-full h-full border-0 bg-white"
              title="Website Preview"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 bg-gray-50/50">
                <div className="p-8 text-center max-w-md">
                   <h2 className="text-2xl font-bold mb-2 text-gray-600">Preview Mode</h2>
                   <p>Your bot will appear in the bottom right corner.</p>
                </div>
            </div>
          )}
        </div>
      )}

      <div className={widgetContainerClass}>
        
        {/* Main Widget Card */}
        {isOpen && (
          <div 
            className={`w-[360px] ${isEmbed ? 'h-[600px]' : 'h-[650px]'} bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100 transition-all duration-300 ease-in-out origin-bottom-right`}
            style={{ 
                fontFamily: 'Inter, sans-serif',
                maxWidth: isEmbed ? 'calc(100vw - 32px)' : '360px',
                maxHeight: isEmbed ? 'calc(100vh - 40px)' : 'none'
            }}
          >
            {/* Header */}
            <div className="relative p-5 text-white shrink-0 overflow-hidden" style={gradientStyle}>
               {/* Decorative Background Circles */}
               <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-xl pointer-events-none"></div>
               <div className="absolute top-10 -left-6 w-20 h-20 bg-white/10 rounded-full blur-lg pointer-events-none"></div>

               <div className="relative z-10 flex items-center justify-between">
                 <div className="flex items-center gap-3">
                   <div className="w-11 h-11 rounded-full border-2 border-white/40 overflow-hidden bg-white/20 shadow-sm backdrop-blur-sm">
                       <img src={config.avatarUrl} alt="Bot" className="w-full h-full object-cover" />
                   </div>
                   <div>
                     <h3 className="font-bold text-lg leading-tight tracking-tight">{config.name}</h3>
                     <div className="flex items-center gap-1.5 mt-0.5">
                       <span className="relative flex h-2 w-2">
                          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isConnected || activeTab === 'chat' ? 'bg-green-300' : 'bg-red-300'}`}></span>
                          <span className={`relative inline-flex rounded-full h-2 w-2 ${isConnected || activeTab === 'chat' ? 'bg-green-400' : 'bg-red-400'}`}></span>
                       </span>
                       <p className="text-xs font-medium text-white/90">
                         {activeTab === 'voice' ? (isConnected ? 'Live Audio' : 'Ready') : 'Online'}
                       </p>
                     </div>
                   </div>
                 </div>
                 <button 
                   onClick={() => setIsOpen(false)} 
                   className="p-1.5 rounded-full hover:bg-white/20 transition-colors text-white/90 hover:text-white"
                 >
                   <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                     <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                   </svg>
                 </button>
               </div>
            </div>

            {/* Content Container */}
            <div className="flex-1 bg-gray-50 relative flex flex-col min-h-0">
               
               {/* Tab Switcher (Floating) */}
               <div className="absolute top-0 left-0 right-0 z-20 px-4 py-3 bg-gradient-to-b from-gray-50 to-gray-50/0">
                 <div className="bg-white p-1 rounded-xl shadow-sm border border-gray-200 flex">
                   <button
                     onClick={() => setActiveTab('voice')}
                     className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                       activeTab === 'voice' 
                         ? 'bg-gray-900 text-white shadow-md' 
                         : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                     }`}
                   >
                     Voice
                   </button>
                   <button
                     onClick={() => setActiveTab('chat')}
                     className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                       activeTab === 'chat' 
                         ? 'bg-gray-900 text-white shadow-md' 
                         : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                     }`}
                   >
                     Text
                   </button>
                 </div>
               </div>

               {/* TAB CONTENT: VOICE */}
               {activeTab === 'voice' && (
                 <div className="flex-1 flex flex-col h-full pt-14 animate-slide-up">
                    
                    {/* Conversation Area */}
                    <div className="flex-1 overflow-y-auto scrollbar-hide px-4 pb-4 space-y-4">
                       {transcripts.length === 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-6 mt-10 opacity-60">
                             <div className="w-24 h-24 bg-gray-200/50 rounded-full flex items-center justify-center mb-4 animate-float">
                               <img src={config.avatarUrl} className="w-20 h-20 rounded-full opacity-50 grayscale" alt="" />
                             </div>
                             <p className="text-gray-500 font-medium">Start a conversation</p>
                             <p className="text-xs text-gray-400 mt-1">Tap the microphone below</p>
                          </div>
                       ) : (
                          transcripts.map((msg, idx) => (
                            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}>
                              <div 
                                className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${
                                  msg.role === 'user' 
                                    ? 'bg-white text-gray-800 border border-gray-100 rounded-br-none' 
                                    : 'bg-white/80 text-gray-600 border border-transparent rounded-bl-none'
                                }`}
                              >
                                {msg.text}
                              </div>
                            </div>
                          ))
                       )}
                       <div ref={messagesEndRef} className="h-4" />
                    </div>

                    {/* Voice Controls Area */}
                    <div className="bg-white rounded-t-3xl shadow-[0_-5px_20px_-5px_rgba(0,0,0,0.05)] p-6 pb-8 border-t border-gray-50 relative z-30">
                       <div className="flex flex-col items-center gap-5">
                          
                          {/* Status Badge - Dynamic Error Display */}
                          <div className={`px-4 py-1.5 rounded-full text-[11px] font-bold transition-colors text-center max-w-[280px] leading-tight ${
                             voiceError ? 'bg-red-50 text-red-600 border border-red-100' :
                             'uppercase tracking-wider ' + (
                               isBotSpeaking ? 'bg-indigo-50 text-indigo-500' :
                               isSpeaking ? 'bg-green-50 text-green-500' :
                               isConnected ? 'bg-gray-50 text-gray-500' :
                               'bg-gray-50 text-gray-400'
                             )
                          }`}>
                             {voiceError || (
                               isBotSpeaking ? 'Bot Speaking' : 
                               isSpeaking ? 'Listening...' : 
                               isConnected ? 'Mic Ready' : 'Tap to Start'
                             )}
                          </div>

                          {/* Visualizer / Main Button */}
                          <div className="relative w-24 h-24 flex items-center justify-center">
                             
                             {/* Error State - Retry Button */}
                             {voiceError && (
                               <button onClick={() => reconnect()} className="absolute z-20 bg-red-50 p-4 rounded-full shadow-lg border border-red-100 hover:bg-red-100 transition group">
                                 <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500 group-hover:rotate-180 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                 </svg>
                               </button>
                             )}

                             {/* Active State (Visualizer) */}
                             {!voiceError && isConnected && (
                                <div className="flex items-center justify-center gap-1.5 h-16">
                                   {[...Array(5)].map((_, i) => (
                                     <div 
                                       key={i}
                                       className="w-2.5 rounded-full transition-all duration-100 ease-in-out"
                                       style={{
                                          backgroundColor: config.themeColor,
                                          // Simulate waveform based on volume. 
                                          // Center bars are taller.
                                          height: `${Math.max(8, volume * (i === 2 ? 180 : i === 1 || i === 3 ? 120 : 80) + (isSpeaking ? Math.random() * 20 : 5))}px`,
                                          opacity: isSpeaking ? 1 : 0.5
                                       }}
                                     />
                                   ))}
                                </div>
                             )}

                             {/* Idle State (Start Button) */}
                             {!voiceError && !isConnected && (
                                <button 
                                  onClick={() => reconnect()}
                                  className="w-20 h-20 bg-gray-900 rounded-full shadow-xl flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-all"
                                  style={{ backgroundColor: config.themeColor }}
                                >
                                   <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                                      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                                   </svg>
                                </button>
                             )}
                          </div>
                       </div>
                    </div>
                 </div>
               )}

               {/* TAB CONTENT: CHAT */}
               {activeTab === 'chat' && (
                 <div className="flex-1 flex flex-col h-full pt-14 animate-slide-up bg-gray-50/50">
                    <div className="flex-1 overflow-y-auto scrollbar-hide px-4 pb-2 space-y-4">
                      {chatMessages.length === 0 ? (
                         <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 mt-20">
                            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-3 rotate-3">
                               <p className="text-sm">👋 Hi there! How can I help?</p>
                            </div>
                         </div>
                      ) : (
                         chatMessages.map((msg, idx) => (
                           <div key={idx} className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}>
                             <div 
                               className={`max-w-[80%] p-3.5 text-sm leading-relaxed shadow-sm relative ${
                                 msg.role === 'user' 
                                   ? 'text-white rounded-2xl rounded-tr-sm' 
                                   : 'bg-white text-gray-800 rounded-2xl rounded-tl-sm border border-gray-100'
                               }`}
                               style={msg.role === 'user' ? gradientStyle : {}}
                             >
                               {msg.text}
                             </div>
                           </div>
                         ))
                      )}

                      {/* Typing Indicator */}
                      {isChatLoading && (
                        <div className="flex justify-start animate-slide-up">
                          <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm p-4 shadow-sm flex gap-1.5 items-center">
                            <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce"></span>
                            <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce delay-100"></span>
                            <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce delay-200"></span>
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Chat Input */}
                    <div className="p-4 bg-white/80 backdrop-blur-md border-t border-gray-100">
                       <form onSubmit={handleSendChat} className="flex gap-2 items-end">
                         <input
                           type="text"
                           value={chatInput}
                           onChange={(e) => setChatInput(e.target.value)}
                           placeholder="Type a message..."
                           className="flex-1 bg-gray-100 border-transparent focus:bg-white focus:border-gray-200 focus:ring-0 rounded-2xl px-4 py-3 text-sm transition-all shadow-inner"
                         />
                         <button 
                           type="submit"
                           disabled={!chatInput.trim() || isChatLoading}
                           className="p-3 rounded-xl text-white shadow-lg shadow-blue-500/20 transition-transform active:scale-95 disabled:opacity-50 disabled:shadow-none"
                           style={gradientStyle}
                         >
                           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                             <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                           </svg>
                         </button>
                       </form>
                    </div>
                 </div>
               )}
            </div>
          </div>
        )}

        {/* Launcher Button */}
        {!isOpen && (
          <div className="relative group z-50">
            <div 
              className="absolute inset-0 rounded-full animate-pulse-ring opacity-50"
              style={{ backgroundColor: config.themeColor }}
            />
            <button
              onClick={() => setIsOpen(true)}
              className="relative w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 overflow-hidden border-[3px] border-white z-10"
              style={gradientStyle}
            >
              {config.avatarUrl ? (
                <img 
                  src={config.avatarUrl} 
                  alt={config.name} 
                  className="w-full h-full object-cover" 
                />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Utility to lighten/darken hex color for gradients
function adjustColorBrightness(hex: string, percent: number) {
    const num = parseInt(hex.replace("#",""), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    G = (num >> 8 & 0x00FF) + amt,
    B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + (G<255?G<1?0:G:255)*0x100 + (B<255?B<1?0:B:255)).toString(16).slice(1);
}

export default WidgetPreview;